import { BunkerSigner, createNostrConnectURI, parseBunkerInput, type BunkerPointer } from 'nostr-tools/nip46';
import type { WindowNostr } from 'nostr-tools/nip07';
import { finalizeEvent, generateSecretKey, getPublicKey, verifyEvent } from 'nostr-tools/pure';
import { decode } from 'nostr-tools/nip19';
import { decrypt as decryptNip49, encrypt as encryptNip49 } from 'nostr-tools/nip49';
import { SimplePool } from 'nostr-tools/pool';
import type { Signer } from 'nostr-tools/signer';
import type { Event, EventTemplate, VerifiedEvent } from 'nostr-tools/core';

const requestedPermissions = [
  'sign_event:27235',
  'sign_event:30009',
  'sign_event:8',
	'sign_event_batch:8',
  'sign_event:5'
];

export const recipientPermissions = ['sign_event:10008'];

declare global {
  interface Window {
    nostr?: WindowNostr;
  }
}

export type ApprovalHandler = (url: string) => boolean | void;
type ApprovalState = { handler?: ApprovalHandler };

export type SignerConnection = {
  signer: Signer;
  signEventWithApproval?: (event: EventTemplate, onApproval: ApprovalHandler) => Promise<VerifiedEvent>;
  pubkey: string;
  method: 'nip07' | 'nip46' | 'nsec';
  label: string;
	ncryptsec?: string;
	authenticationToken?: string;
	managedSession?: ManagedSignerSession;
	signEvents?: (events: EventTemplate[], context?: BadgeBatchContext) => Promise<VerifiedEvent[]>;
	logout?: () => Promise<void>;
  close(): Promise<void>;
};

export type ManagedSignerSession = {
	bunkerURL: string;
	clientSecret?: string;
	pubkey: string;
};

export type BadgeBatchContext = {
	organization_id: string;
	recipients: Array<{
		pubkey: string;
		person_id?: string;
		grant_id?: string;
		subject_profile_url?: string;
	}>;
};

export type PendingNostrConnect = {
  uri: string;
  connected: Promise<SignerConnection>;
  cancel(): void;
};

export async function connectNIP07(): Promise<SignerConnection> {
  if (!window.nostr) throw new Error('No NIP-07 browser signer was detected.');
  const signer = window.nostr;
  const pubkey = await signer.getPublicKey();
  return { signer, pubkey, method: 'nip07', label: 'Browser extension', close: async () => {} };
}

export async function connectNsec(value: string, persistencePassphrase = ''): Promise<SignerConnection> {
  const decoded = decode(value.trim());
  if (decoded.type !== 'nsec') throw new Error('Enter a valid nsec secret key.');
  const secret = decoded.data;
  if (secret.length !== 32) {
    secret.fill(0);
    throw new Error('That nsec does not contain a valid secret key.');
  }
  const ncryptsec = persistencePassphrase ? encryptNip49(secret, persistencePassphrase) : undefined;
	return localSecretConnection(secret, ncryptsec);
}

function localSecretConnection(secret: Uint8Array, ncryptsec?: string): SignerConnection {
  const pubkey = getPublicKey(secret);
  let closed = false;
  const signer: Signer = {
    getPublicKey: async () => {
      if (closed) throw new Error('The local key has been cleared. Reconnect it to sign again.');
      return pubkey;
    },
    signEvent: async (event: EventTemplate): Promise<VerifiedEvent> => {
      if (closed) throw new Error('The local key has been cleared. Reconnect it to sign again.');
      return finalizeEvent(event, secret);
    }
  };
  return {
    signer,
    pubkey,
    method: 'nsec',
    label: 'Local nsec · memory only',
		ncryptsec,
    close: async () => { secret.fill(0); closed = true; }
  };
}

export async function connectNcryptsec(value: string, passphrase: string): Promise<SignerConnection> {
  if (!value.startsWith('ncryptsec1')) throw new Error('The saved key is not a valid NIP-49 encrypted secret.');
  if (!passphrase) throw new Error('Enter the passphrase for this saved key.');
  let secret: Uint8Array;
  try {
    secret = decryptNip49(value, passphrase);
  } catch {
    throw new Error('That passphrase could not unlock the saved key.');
  }
  return localSecretConnection(secret);
}

export async function connectBunker(input: string, signal?: AbortSignal, permissions = requestedPermissions, onApproval?: (url: string) => boolean | void): Promise<SignerConnection> {
	const candidate = input.trim();
	const pointer = await parseBunkerInput(candidate);
  if (!pointer) throw new Error('Enter a valid bunker:// connection string or NIP-05 address.');
  if (!pointer.relays.length) throw new Error('This bunker link has no relay. Copy a fresh connection link from your vault.');

  const clientSecret = generateSecretKey();
  const pool = new SimplePool();
  const approval: ApprovalState = { handler: onApproval };
  const signer = BunkerSigner.fromBunker(clientSecret, pointer, { pool, onauth: (url) => {
    if (!signal?.aborted) openAuthURL(url, approval.handler);
  } });
  try {
	const managed = hasManagedLoginCapability(candidate);
	const combinedLogin = managed && permissions.includes('sign_event:27235');
	let combinedLoginEvent: Event | undefined;
		if (combinedLogin) {
			const endpoint = new URL('/api/auth/session', window.location.origin).toString();
			const metadata = { name: 'Bitcoin++ Badge Studio', url: window.location.origin, btcpp_login: { url: endpoint, method: 'POST' } };
			const response = await abortable(signer.sendRequest('connect', [pointer.pubkey, pointer.secret ?? '', permissions.join(','), JSON.stringify(metadata)]), signal);
		const combined: { connection?: string; login_event?: Event } = JSON.parse(response);
		if (combined.connection !== 'ack' || !combined.login_event || !verifyEvent(combined.login_event)) throw new Error('The managed signer returned an invalid combined login.');
		combinedLoginEvent = combined.login_event;
		} else {
			const metadata = { name: 'Bitcoin++ Badge Studio', url: window.location.origin };
			await abortable(signer.sendRequest('connect', [pointer.pubkey, pointer.secret ?? '', permissions.join(','), JSON.stringify(metadata)]), signal);
	}
    const pubkey = await abortable(signer.getPublicKey(), signal);
		let authenticationToken: string | undefined;
		if (combinedLoginEvent) {
			const endpoint = new URL('/api/auth/session', window.location.origin).toString();
			const urlTags = combinedLoginEvent.tags.filter((tag) => tag[0] === 'u');
			const methodTags = combinedLoginEvent.tags.filter((tag) => tag[0] === 'method');
			if (combinedLoginEvent.pubkey !== pubkey || combinedLoginEvent.kind !== 27235 || combinedLoginEvent.content !== '' || combinedLoginEvent.tags.length !== 2 || urlTags.length !== 1 || urlTags[0][1] !== endpoint || methodTags.length !== 1 || methodTags[0][1] !== 'POST') {
				throw new Error('The managed signer returned a login for a different account or destination.');
			}
			authenticationToken = `Nostr ${btoa(JSON.stringify(combinedLoginEvent))}`;
		}
		const connection = remoteConnection(signer, pool, pubkey, hasManagedBatchCapability(candidate), authenticationToken, clientSecret, approval);
		if (managed) connection.managedSession = { bunkerURL: candidate, clientSecret: encodeSecret(clientSecret), pubkey };
		return connection;
  } catch (error) {
    await signer.close();
    pool.close(pointer.relays);
		clientSecret.fill(0);
    throw bunkerConnectionError(error, pointer.relays);
  }
}

export async function restoreManagedBunker(session: ManagedSignerSession, signal?: AbortSignal): Promise<SignerConnection> {
	const candidate = session.bunkerURL.trim();
	if (!hasManagedLoginCapability(candidate)) throw new Error('The saved managed signer address is invalid.');
	const pointer = await parseBunkerInput(candidate);
	if (!pointer) throw new Error('The saved managed signer address is invalid.');
	if (!session.clientSecret) throw new Error('This managed signer needs a new Badge Studio connection.');
	const clientSecret = decodeSecret(session.clientSecret);
	const pool = new SimplePool();
	const approval: ApprovalState = {};
	const signer = BunkerSigner.fromBunker(clientSecret, pointer, { pool, onauth: (url) => openAuthURL(url, approval.handler) });
	try {
		const pubkey = await abortable(signer.getPublicKey(), signal);
		if (pubkey !== session.pubkey) throw new Error('The managed signer now represents a different organization key.');
		const connection = remoteConnection(signer, pool, pubkey, hasManagedBatchCapability(candidate), undefined, clientSecret, approval);
		connection.managedSession = { bunkerURL: candidate, clientSecret: session.clientSecret, pubkey };
		return connection;
	} catch (error) {
		await signer.close();
		pool.close(pointer.relays);
		clientSecret.fill(0);
		throw bunkerConnectionError(error, pointer.relays);
	}
}

export function bunkerConnectionError(error: unknown, relays: string[]): unknown {
  if (!(error instanceof AggregateError)) return error;
  const hosts = relays.map(relay => {
    try { return new URL(relay).hostname; } catch { return "invalid relay"; }
  }).join(", ");
  return new Error(`The connection request could not be delivered through any signing relay${hosts ? ` (${hosts})` : ""}. A relay may be offline, unreachable, or rejecting requests. Retry, or ask the vault administrator to check its relay configuration.`);
}

async function abortable<T>(operation: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return operation;
  signal.throwIfAborted();
  return Promise.race([
    operation,
    new Promise<T>((_, reject) => signal.addEventListener('abort', () => reject(signal.reason), { once: true }))
  ]);
}

export function beginNostrConnect(relays: string[], permissions = requestedPermissions, applicationName = 'Bitcoin++ Badge Studio', onApproval?: (url: string) => boolean | void): PendingNostrConnect {
  if (relays.length === 0) throw new Error('At least one NIP-46 relay is required.');
  const clientSecret = generateSecretKey();
  const secret = randomToken();
  const uri = createNostrConnectURI({
    clientPubkey: getPublicKey(clientSecret),
    relays,
    secret,
    perms: permissions,
    name: applicationName,
    url: window.location.origin
  });
  const controller = new AbortController();
  const pool = new SimplePool();
  const approval: ApprovalState = { handler: onApproval };
  const connected = BunkerSigner.fromURI(
    clientSecret,
    uri,
    { pool, onauth: (url) => {
      if (!controller.signal.aborted) openAuthURL(url, approval.handler);
    } },
    controller.signal
  ).then(async (signer) => {
    const pubkey = await signer.getPublicKey();
		return remoteConnection(signer, pool, pubkey, false, undefined, clientSecret, approval);
  }).catch((error) => {
		clientSecret.fill(0);
    pool.close(relays);
    throw error;
  });

  return { uri, connected, cancel: () => controller.abort() };
}

function remoteConnection(signer: BunkerSigner, pool: SimplePool, pubkey: string, managedBatch = false, authenticationToken?: string, clientSecret?: Uint8Array, approval?: ApprovalState): SignerConnection {
	const connection: SignerConnection = {
    signer,
    signEventWithApproval: async (event, onApproval) => {
      const previous = approval?.handler;
      if (approval) approval.handler = onApproval;
      try { return await signer.signEvent(event); }
      finally { if (approval) approval.handler = previous; }
    },
    pubkey,
    method: 'nip46',
    label: 'Remote signer',
			authenticationToken,
		logout: async () => {
			try {
				await signer.logout();
			} finally {
				pool.close(signer.bp.relays);
				clientSecret?.fill(0);
			}
		},
	    close: async () => {
      await signer.close();
      pool.close(signer.bp.relays);
			clientSecret?.fill(0);
    }
  };
	if (managedBatch) {
		connection.signEvents = async (events, context) => {
			const response = await signer.sendRequest('sign_event_batch', [JSON.stringify({ events, context })]);
			const signed: Event[] = JSON.parse(response);
			if (!Array.isArray(signed) || signed.length !== events.length || !signed.every((event) => verifyEvent(event))) {
				throw new Error('The managed signer returned an invalid badge batch.');
			}
			if (signed.some((event) => event.pubkey !== pubkey)) throw new Error('The managed signer returned events for another identity.');
			return signed;
		};
	}
	return connection;
}

function hasManagedLoginCapability(input: string): boolean {
	try {
		return input.startsWith('bunker://') && new URL(input).searchParams.get('x-btcpp-login') === '1';
	} catch {
		return false;
	}
}

function hasManagedBatchCapability(input: string): boolean {
	try {
		return input.startsWith('bunker://') && new URL(input).searchParams.get('x-btcpp-batch') === '1';
	} catch {
		return false;
	}
}

function randomToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
}

function encodeSecret(secret: Uint8Array): string {
	return Array.from(secret, (value) => value.toString(16).padStart(2, '0')).join('');
}

function decodeSecret(value: string): Uint8Array {
	if (!/^[0-9a-f]{64}$/.test(value)) throw new Error('The saved managed signer credential is invalid.');
	return Uint8Array.from(value.match(/.{2}/g)!, (byte) => Number.parseInt(byte, 16));
}

export function openAuthURL(value: string, onApproval?: (url: string) => boolean | void): void {
  const url = new URL(value);
  if (url.protocol !== 'https:' && !(url.protocol === 'http:' && url.hostname === 'localhost')) {
    throw new Error('The remote signer returned an unsafe authorization URL.');
  }
  // Always offer a direct link: asynchronous popups can be blocked, and
  // noopener makes the return value unreliable for detecting that case.
  if (onApproval?.(url.href) === true) return;
  window.open(url, '_blank', 'noopener,noreferrer');
}

export type { BunkerPointer };
